import {
  LearningObjectiveStatementStatus,
  OrderType,
  useSearchTaskLearningObjectiveStatementsLazyQuery,
} from '@exam-management/graphql';
import { useQuestionCreationTaskDetails } from '@exam-management/question-creation-task-details';
import {
  FormSearchSelection,
  type FormSearchSelectionProps,
  type OptionSelectionProps,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext, type FieldValues } from 'react-hook-form';

export interface LearningObjectiveStatementFieldProps<TFieldValues extends FieldValues>
  extends Omit<FormSearchSelectionProps<TFieldValues>, 'options' | 'label'> {
  chapterLevelItemId: string | null;
  helperText?: React.ReactNode;
}

export default function LearningObjectiveStatementField<TFieldValues extends FieldValues>(
  props: Readonly<LearningObjectiveStatementFieldProps<TFieldValues>>,
) {
  const { name, helperText, chapterLevelItemId, ...restProps } = props;
  const { task } = useQuestionCreationTaskDetails();
  const [isOpen, { open, close }] = useDisclosure();
  const [losOptions, setLosOptions] = useState<OptionSelectionProps[]>([]);

  const { setValue } = useFormContext();

  const [searchLOSResult, { loading: isSearchLOSLoading }] =
    useSearchTaskLearningObjectiveStatementsLazyQuery({
      onCompleted: (data) => {
        const losOptions =
          data.qbsSearchTaskLearningObjectiveStatements.learningObjectiveStatements.map((los) => {
            return { label: los.tag.name, value: los.id };
          }) ?? [];
        setLosOptions(losOptions);
      },
      fetchPolicy: 'network-only',
    });

  const handleOpenOptions = useCallback(async () => {
    if (chapterLevelItemId) {
      await searchLOSResult({
        variables: {
          search: {
            chapterLevelItemId,
            status: LearningObjectiveStatementStatus.Published,
          },
          paging: {
            pageSize: 100,
            currentPage: 0,
          },
          order: {
            field: 'updatedAt',
            type: OrderType.Asc,
          },
          taskId: task.id,
        },
      });
    }
    open();
  }, [chapterLevelItemId, open, searchLOSResult, task.id]);

  const noOptionsText = helperText ?? 'ไม่พบข้อมูล';
  const shouldDisabled = !chapterLevelItemId;

  useEffect(() => {
    if (!chapterLevelItemId) {
      setLosOptions([]);
      setValue(name as Parameters<typeof setValue>[0], null);
    }
  }, [chapterLevelItemId, name, setValue]);

  return (
    <FormSearchSelection
      label='วัตถุประสงค์การเรียนรู้ (Learning Objective Statement (LOS))'
      options={losOptions}
      name={name}
      placeholder='กรุณาเลือกวัตถุประสงค์การเรียนรู้ (Learning Objective Statement (LOS))'
      loading={isSearchLOSLoading}
      noOptionsText={noOptionsText}
      open={isOpen}
      onOpen={handleOpenOptions}
      disabled={shouldDisabled}
      onClose={close}
      {...restProps}
    />
  );
}
